import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaCoins,
  FaPlay,
  FaInfo,
  FaCrown,
  FaSync,
  FaGift,
  FaStar,
  FaTimes,
  FaExclamationTriangle,
  FaArrowRight,
  FaCheckCircle,
  FaBolt,
} from "react-icons/fa";
import LuckyKeypad from "./LuckyKeypad";
import LootCapsule from "./LootCapsule";
import { primeAnimationAudio } from "../../hooks/useAnimationSound";
import { useBatchCandyMachine } from "../../hooks/useBatchCandyMachine";
import { useRadixConnect } from "../../../radix_connect";
import {
  RADIX_API_GATEWAY_URL,
  DCKSLAP_RESOURCE_ADDRESS,
  RADIX_TOKEN_RESOURCE_ADDRESS,
} from "../../../constants";
import "./CandyMachine.css";

const GAME_STATUS = {
  IDLE: "IDLE",
  PROCESSING_TX: "PROCESSING_TX",
  AWAITING_BOT: "AWAITING_BOT",
  REVEAL: "REVEAL",
  TIMEOUT: "TIMEOUT",
};

const BOT_MESSAGES = [
  "Waiting for Dispenser Bot...",
  "Calibrating Luck...",
  "Rolling the dice...",
  "Consulting the RNG gods...",
  "Processing your destiny...",
];

const truncateHash = (hash) => {
  if (!hash || hash.length < 18) {
    return hash || "";
  }
  return `${hash.slice(0, 10)}...${hash.slice(-8)}`;
};

const CandyMachine = ({
  onPlay,
  gameStatus = GAME_STATUS.IDLE,
  lastReward = null,
  userLevel = 0,
  onClearReward,
  onViewRules,
  onResetStatus,
  disabled = false,
}) => {
  const navigate = useNavigate();
  const { selectedAccount } = useRadixConnect();

  const {
    gameStatus: batchGameStatus,
    lastBatchReceipt,
    batchSize,
    reddicksFee,
    xrdFee,
    playBatchRound,
    checkBatchBalances,
    resetGameStatus: resetBatchGameStatus,
    clearReceipt: clearBatchReceipt,
    GAME_STATUS: BATCH_GAME_STATUS,
  } = useBatchCandyMachine();

  const [selectedChoice, setSelectedChoice] = useState(null);
  const [showKeypad, setShowKeypad] = useState(false);
  const [showCapsule, setShowCapsule] = useState(false);
  const [isShaking, setIsShaking] = useState(false);
  const [botMessage, setBotMessage] = useState("");
  const [showInsufficientTokensPopup, setShowInsufficientTokensPopup] =
    useState(false);
  const [insufficientMode, setInsufficientMode] = useState("single");
  const [missingToken, setMissingToken] = useState(null);
  const [batchMissingToken, setBatchMissingToken] = useState(null);
  const [isCheckingBalance, setIsCheckingBalance] = useState(false);
  const [isCheckingBatchBalances, setIsCheckingBatchBalances] = useState(false);
  const [batchBalancePreview, setBatchBalancePreview] = useState(null);
  const [batchRewardsQueue, setBatchRewardsQueue] = useState([]);
  const [currentBatchReward, setCurrentBatchReward] = useState(null);
  const [capsuleKey, setCapsuleKey] = useState(0);

  const isSingleProcessing =
    gameStatus !== GAME_STATUS.IDLE && gameStatus !== GAME_STATUS.TIMEOUT;
  const isAwaitingBot = gameStatus === GAME_STATUS.AWAITING_BOT;
  const isTimeout = gameStatus === GAME_STATUS.TIMEOUT;

  const isBatchProcessing =
    batchGameStatus === BATCH_GAME_STATUS.PROCESSING_TX ||
    batchGameStatus === BATCH_GAME_STATUS.VERIFYING_EVENT;
  const isBatchTimeout = batchGameStatus === BATCH_GAME_STATUS.TIMEOUT;
  const isBatchConfirmed = batchGameStatus === BATCH_GAME_STATUS.CONFIRMED;

  const isAnyProcessing =
    isSingleProcessing ||
    isBatchProcessing ||
    isCheckingBalance ||
    isCheckingBatchBalances;

  useEffect(() => {
    if (isAwaitingBot) {
      let index = 0;
      setBotMessage(BOT_MESSAGES[0]);
      const interval = setInterval(() => {
        index = (index + 1) % BOT_MESSAGES.length;
        setBotMessage(BOT_MESSAGES[index]);
      }, 2000);
      return () => clearInterval(interval);
    }
    return undefined;
  }, [isAwaitingBot]);

  useEffect(() => {
    if (gameStatus === GAME_STATUS.REVEAL) {
      setShowCapsule(true);
      setIsShaking(false);
      return;
    }

    if (
      gameStatus === GAME_STATUS.PROCESSING_TX ||
      gameStatus === GAME_STATUS.AWAITING_BOT ||
      isBatchProcessing
    ) {
      setIsShaking(true);
      return;
    }

    setIsShaking(false);
  }, [gameStatus, isBatchProcessing]);

  useEffect(() => {
    if (isBatchConfirmed && lastBatchReceipt) {
      const rewards = lastBatchReceipt.rewards || [];
      if (rewards.length > 0) {
        setCurrentBatchReward(rewards[0]);
        setBatchRewardsQueue(rewards.slice(1));
      } else {
        setCurrentBatchReward(null);
        setBatchRewardsQueue([]);
      }
      setShowCapsule(true);
      setIsShaking(false);
    }
  }, [isBatchConfirmed, lastBatchReceipt]);

  const handleChoiceSelect = useCallback(
    (choice) => {
      setSelectedChoice(choice);
      setShowKeypad(false);

      if (isBatchConfirmed || isBatchTimeout) {
        clearBatchReceipt();
        resetBatchGameStatus();
      }
    },
    [
      clearBatchReceipt,
      isBatchConfirmed,
      isBatchTimeout,
      resetBatchGameStatus,
    ]
  );

  const refreshBatchBalances = useCallback(async () => {
    setIsCheckingBatchBalances(true);
    try {
      const result = await checkBatchBalances();
      setBatchBalancePreview(result);
      return result;
    } finally {
      setIsCheckingBatchBalances(false);
    }
  }, [checkBatchBalances]);

  useEffect(() => {
    if (selectedAccount?.address) {
      refreshBatchBalances();
      return;
    }
    setBatchBalancePreview(null);
  }, [refreshBatchBalances, selectedAccount]);

  const checkTokenBalances = useCallback(async () => {
    if (!selectedAccount?.address) return { hasEnough: false, missing: "BOTH" };

    try {
      let dckslapBalance = 0;
      let xrdBalance = 0;
      let cursor = null;
      let foundDckslap = false;
      let foundXrd = false;
      let stateVersion = null;

      do {
        const requestBody = {
          address: selectedAccount.address,
        };
        if (cursor) {
          requestBody.cursor = cursor;
          requestBody.at_ledger_state = { state_version: stateVersion };
        }

        const response = await fetch(
          `${RADIX_API_GATEWAY_URL}/state/entity/page/fungibles/`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(requestBody),
          }
        );

        if (!response.ok) {
          return { hasEnough: false, missing: "BOTH" };
        }

        const data = await response.json();
        const items = data.items || [];

        for (const item of items) {
          if (item.resource_address === DCKSLAP_RESOURCE_ADDRESS) {
            dckslapBalance = Number.parseFloat(item.amount) || 0;
            foundDckslap = true;
          } else if (item.resource_address === RADIX_TOKEN_RESOURCE_ADDRESS) {
            xrdBalance = Number.parseFloat(item.amount) || 0;
            foundXrd = true;
          }

          if (foundDckslap && foundXrd) break;
        }

        cursor = data.next_cursor || null;
        stateVersion = data.ledger_state?.state_version;
      } while (cursor && !(foundDckslap && foundXrd));

      const hasDckslap = dckslapBalance >= 1;
      const hasXrd = xrdBalance >= 0.5;

      if (hasDckslap && hasXrd) {
        return { hasEnough: true, missing: null };
      }
      if (!hasDckslap && !hasXrd) {
        return { hasEnough: false, missing: "BOTH" };
      }
      if (!hasDckslap) {
        return { hasEnough: false, missing: "DCKSLAP" };
      }
      return { hasEnough: false, missing: "XRD" };
    } catch (error) {
      console.error("Error checking token balances:", error);
      return { hasEnough: false, missing: "BOTH" };
    }
  }, [selectedAccount]);

  const handlePlay = useCallback(async () => {
    if (!selectedChoice || isAnyProcessing || disabled) return;

    if (isBatchConfirmed || isBatchTimeout) {
      clearBatchReceipt();
      resetBatchGameStatus();
    }

    setIsCheckingBalance(true);
    try {
      const { hasEnough, missing } = await checkTokenBalances();

      if (!hasEnough) {
        setInsufficientMode("single");
        setMissingToken(missing);
        setShowInsufficientTokensPopup(true);
        return;
      }

      primeAnimationAudio();

      if (onPlay) {
        await onPlay(selectedChoice);
      }
    } finally {
      setIsCheckingBalance(false);
    }
  }, [
    selectedChoice,
    isAnyProcessing,
    disabled,
    checkTokenBalances,
    onPlay,
    isBatchConfirmed,
    isBatchTimeout,
    clearBatchReceipt,
    resetBatchGameStatus,
  ]);

  const handleBatchPlay = useCallback(async () => {
    if (!selectedChoice || isAnyProcessing || disabled) return;

    const balances = await refreshBatchBalances();

    if (!balances?.hasEnough) {
      setInsufficientMode("batch");
      setBatchMissingToken(balances?.missing || "ALL");
      setShowInsufficientTokensPopup(true);
      return;
    }

    primeAnimationAudio();
    const result = await playBatchRound(selectedChoice);

    if (result?.success) {
      await refreshBatchBalances();
    }
  }, [
    selectedChoice,
    isAnyProcessing,
    disabled,
    refreshBatchBalances,
    playBatchRound,
  ]);

  const handleGoToSlapGenerator = useCallback(() => {
    setShowInsufficientTokensPopup(false);
    navigate("/dckslap/slapgenerator");
  }, [navigate]);

  const handleCloseInsufficientPopup = useCallback(() => {
    setShowInsufficientTokensPopup(false);
  }, []);

  const handleCapsuleClose = useCallback(() => {
    if (batchRewardsQueue.length > 0) {
      setCurrentBatchReward(batchRewardsQueue[0]);
      setBatchRewardsQueue((prev) => prev.slice(1));
      setCapsuleKey((prev) => prev + 1);
      return;
    }

    setShowCapsule(false);
    setCurrentBatchReward(null);
    setSelectedChoice(null);
    if (isBatchConfirmed) {
      clearBatchReceipt();
    }
    if (onClearReward) {
      onClearReward();
    }
  }, [onClearReward, batchRewardsQueue, isBatchConfirmed, clearBatchReceipt]);

  const handleRetry = useCallback(() => {
    if (onResetStatus) {
      onResetStatus();
    }
  }, [onResetStatus]);

  const handleRetryBatch = useCallback(() => {
    resetBatchGameStatus();
  }, [resetBatchGameStatus]);

  const handleOpenKeypad = useCallback(() => {
    if (!isAnyProcessing && !disabled) {
      setShowKeypad(true);
    }
  }, [isAnyProcessing, disabled]);

  const getSingleStatusMessage = () => {
    switch (gameStatus) {
      case GAME_STATUS.PROCESSING_TX:
        return "Submitting Transaction...";
      case GAME_STATUS.AWAITING_BOT:
        return botMessage;
      case GAME_STATUS.REVEAL:
        return "Prize Incoming!";
      case GAME_STATUS.TIMEOUT:
        return "Prize arriving soon... check wallet";
      default:
        return null;
    }
  };

  const getBatchStatusMessage = () => {
    switch (batchGameStatus) {
      case BATCH_GAME_STATUS.PROCESSING_TX:
        return "Submitting batch deposit...";
      case BATCH_GAME_STATUS.VERIFYING_EVENT:
        return "Verifying batch event on-ledger...";
      case BATCH_GAME_STATUS.CONFIRMED:
        return "Batch deposit verified.";
      case BATCH_GAME_STATUS.TIMEOUT:
        return "Batch verification timed out.";
      default:
        return null;
    }
  };

  const getStatusMessage = () => getBatchStatusMessage() || getSingleStatusMessage();

  const getBatchMissingMessage = () => {
    const required = batchBalancePreview?.required || {
      dckslap: Number(batchSize),
      reddicks: Number(reddicksFee),
      xrd: Number(xrdFee),
    };

    if (!batchMissingToken || batchMissingToken === "ALL") {
      return `You need ${required.dckslap} DCKSLAP, ${required.reddicks} REDDICKS and ${required.xrd} XRD (+ network fee).`;
    }

    const parts = batchMissingToken.split("_");
    const missing = [];
    if (parts.includes("DCKSLAP")) missing.push(`${required.dckslap} DCKSLAP`);
    if (parts.includes("REDDICKS")) missing.push(`${required.reddicks} REDDICKS`);
    if (parts.includes("XRD")) missing.push(`${required.xrd} XRD (+ network fee)`);
    return `Missing: ${missing.join(", ")}.`;
  };

  const getInsufficientMessage = () => {
    if (insufficientMode === "batch") {
      return getBatchMissingMessage();
    }

    if (missingToken === "BOTH") {
      return "You need at least 1 DCKSLAP and 0.5 XRD to play.";
    }
    if (missingToken === "DCKSLAP") {
      return "You need at least 1 DCKSLAP to play.";
    }
    return "You need at least 0.5 XRD for the protocol fee.";
  };

  const shouldShowGetTokens =
    insufficientMode === "single"
      ? missingToken !== "XRD"
      : !String(batchMissingToken || "").includes("XRD");

  const currentStatusMessage = getStatusMessage();
  const batchPreviewRows = [
    {
      token: "Dckslap",
      available: batchBalancePreview?.balances?.dckslap ?? 0,
      required: Number(batchSize),
    },
    {
      token: "Reddicks",
      available: batchBalancePreview?.balances?.reddicks ?? 0,
      required: Number(reddicksFee),
    },
    {
      token: "XRD",
      available: batchBalancePreview?.balances?.xrd ?? 0,
      required: Number(xrdFee),
    },
  ];

  const batchInlineText = (() => {
    if (isBatchProcessing) return "Batch processing...";
    if (isBatchConfirmed && lastBatchReceipt) {
      const rewards = lastBatchReceipt.rewards || [];
      const txCount = lastBatchReceipt.totalTransactions || 0;
      if (rewards.length > 0) {
        const rewardSummary = rewards
          .map((r) => `${r.name} (${r.rarity})`)
          .join(", ");
        return `${rewards.length} candy${rewards.length > 1 ? "s" : ""} from ${txCount}/${batchSize} pulls: ${rewardSummary}`;
      }
      return `0 candies from ${txCount}/${batchSize} pulls (dry run)`;
    }
    if (isBatchTimeout) return "Batch verification timeout. Retry.";
    return null;
  })();

  return (
    <div
      className={`candy-machine-wrapper ${isShaking ? "shaking" : ""} ${isAnyProcessing ? "processing" : ""
        } ${isAwaitingBot ? "awaiting" : ""} ${isTimeout ? "timeout" : ""} ${isBatchProcessing ? "batch-processing" : ""
        } ${isBatchConfirmed ? "batch-confirmed" : ""} ${isBatchTimeout ? "batch-timeout" : ""
        } ${showKeypad ? "keypad-open" : ""} ${showCapsule ? "capsule-open" : ""
        }`}
    >
      <div className="candy-machine">
        <div className="machine-header">
          <div className="header-lights">
            {Array.from({ length: 8 }).map((_, i) => (
              <div
                key={i}
                className={`light ${isAnyProcessing ? "active" : ""}`}
                style={{ animationDelay: `${i * 0.1}s` }}
              />
            ))}
          </div>
          <div className="header-title">
            <FaGift className="title-icon" />
            <span>CANDY DISPENSER</span>
            <FaStar className="title-icon" />
          </div>
          <div className="header-subtitle">
            Single: 1 DCKSLAP + XRD | Batch: {batchSize} DCKSLAP + {reddicksFee} REDDICKS +{" "}
            {xrdFee} XRD
          </div>
        </div>

        <div className="machine-display">
          <div className="display-screen">
            <div className="status-section">
              <div className="level-badge">
                <FaCrown />
                <span>LVL {userLevel}</span>
              </div>
              <div className="choice-display">
                {currentStatusMessage ? (
                  <div
                    className={`status-message ${isBatchConfirmed ? "status-message-batch-confirmed" : ""
                      } ${isBatchTimeout ? "status-message-batch-timeout" : ""}`}
                  >
                    {(isAwaitingBot || isBatchProcessing) && (
                      <FaSync className="spin-icon" />
                    )}
                    {isBatchConfirmed && <FaCheckCircle className="status-ok-icon" />}
                    <span className="status-text">{currentStatusMessage}</span>
                  </div>
                ) : selectedChoice ? (
                  <>
                    <span className="choice-label">Your Vector</span>
                    <span className="choice-value">{selectedChoice}</span>
                  </>
                ) : (
                  <span className="choice-placeholder">Select a number</span>
                )}
              </div>
            </div>

            <div className="candy-preview">
              <div className="preview-capsules">
                {["common", "rare", "epic", "legendary"].map((rarity, i) => (
                  <div
                    key={rarity}
                    className={`preview-capsule ${rarity}`}
                    style={{ animationDelay: `${i * 0.2}s` }}
                  >
                    <div className="capsule-top" />
                    <div className="capsule-bottom" />
                  </div>
                ))}
              </div>
              <div className="preview-text">Mystery Awaits...</div>
            </div>

            <div className="scanlines" />
          </div>
        </div>

        <div className="machine-controls">
          <div className="batch-dispenser-box">
            <div className="batch-balance-header">
              <div className="batch-balance-title">
                <FaBolt />
                <span>Batch Dispenser</span>
              </div>
              <button
                className="batch-balance-refresh"
                onClick={refreshBatchBalances}
                disabled={isAnyProcessing}
              >
                <FaSync className={isCheckingBatchBalances ? "spin-icon" : ""} />
                <span>{isCheckingBatchBalances ? "..." : "Refresh"}</span>
              </button>
            </div>
            <div className="batch-dispenser-body">
              <div className="batch-balance-rows">
                {batchPreviewRows.map((row) => {
                  const enough = Number(row.available) >= Number(row.required);
                  return (
                    <div
                      key={row.token}
                      className={`batch-balance-chip ${enough ? "ok" : "low"}`}
                    >
                      <span className="chip-label">{row.token}</span>
                      <span className="chip-value">
                        {row.available.toFixed(1)}
                      </span>
                      <span className="chip-req">/ {row.required}</span>
                    </div>
                  );
                })}
              </div>
              <div className="batch-action">
                {isBatchTimeout ? (
                  <button className="batch-retry-button" onClick={handleRetryBatch}>
                    <div className="button-inner">
                      <FaSync className="retry-icon" />
                      <span>RETRY</span>
                    </div>
                    <div className="button-glow" />
                  </button>
                ) : (
                  <button
                    className={`batch-button ${!selectedChoice || isAnyProcessing ? "disabled" : ""
                      }`}
                    onClick={handleBatchPlay}
                    disabled={!selectedChoice || isAnyProcessing || disabled}
                  >
                    <div className="button-inner">
                      {isCheckingBatchBalances ? (
                        <>
                          <FaSync className="play-icon spin-icon" />
                          <span>CHECK</span>
                        </>
                      ) : (
                        <>
                          <FaBolt className="play-icon" />
                          <span>x{batchSize}</span>
                        </>
                      )}
                    </div>
                    <div className="button-glow" />
                  </button>
                )}
              </div>
            </div>
            {batchInlineText && (
              <div
                className={`batch-inline-status ${isBatchConfirmed ? "confirmed" : ""
                  } ${isBatchTimeout ? "timeout" : ""}`}
              >
                <span>{batchInlineText}</span>
                {lastBatchReceipt && (
                  <em>
                    {truncateHash(lastBatchReceipt.transactionIntentHash)} | vector{" "}
                    {lastBatchReceipt.choice}
                  </em>
                )}
                {isBatchConfirmed && (
                  <button
                    className="batch-inline-clear"
                    onClick={clearBatchReceipt}
                    disabled={isAnyProcessing}
                  >
                    clear
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="machine-controls-main">
            <div
              className={`coin-slot ${selectedChoice ? "ready" : ""} ${isAnyProcessing ? "inserting" : ""
                }`}
              onClick={handleOpenKeypad}
            >
              <div className="slot-frame">
                <div className="slot-opening">
                  <FaCoins className="coin-icon" />
                </div>
              </div>
              <span className="slot-label">
                {isAnyProcessing
                  ? "BUSY..."
                  : selectedChoice
                    ? "READY"
                    : "TAP TO SELECT"}
              </span>
            </div>

            {isTimeout ? (
              <button className="retry-button" onClick={handleRetry}>
                <div className="button-inner">
                  <FaSync className="retry-icon" />
                  <span>TRY AGAIN</span>
                </div>
                <div className="button-glow" />
              </button>
            ) : (
              <button
                className={`play-button ${!selectedChoice || isAnyProcessing ? "disabled" : ""
                  }`}
                onClick={handlePlay}
                disabled={!selectedChoice || isAnyProcessing || disabled}
              >
                <div className="button-inner">
                  {isCheckingBalance ? (
                    <>
                      <FaSync className="play-icon spin-icon" />
                      <span>CHECK</span>
                    </>
                  ) : (
                    <>
                      <FaPlay className="play-icon" />
                      <span>DISPENSE</span>
                    </>
                  )}
                </div>
                <div className="button-glow" />
              </button>
            )}

            <button
              className="info-button"
              onClick={onViewRules}
              title="View Odds & Rules"
            >
              <FaInfo />
            </button>
          </div>
        </div>

        <div className="dispenser-bay">
          <div className="bay-opening">
            <div className="bay-flap" />
          </div>
          <div className="bay-label">PRIZE OUTPUT</div>
        </div>

        <div className="machine-decor">
          <div className="decor-stripe stripe-1" />
          <div className="decor-stripe stripe-2" />
          <div className="machine-feet">
            <div className="foot left" />
            <div className="foot right" />
          </div>
        </div>

        <div className="side-vent left">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="vent-slat" />
          ))}
        </div>
        <div className="side-vent right">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="vent-slat" />
          ))}
        </div>
      </div>

      {showKeypad && (
        <div className="keypad-modal">
          <div className="keypad-backdrop" onClick={() => setShowKeypad(false)} />
          <div className="keypad-content">
            <h3 className="keypad-title">Choose Your Lucky Vector</h3>
            <p className="keypad-subtitle">Pick any number from 1-65535</p>
            <LuckyKeypad onSelect={handleChoiceSelect} disabled={isAnyProcessing} />
            <button className="keypad-cancel" onClick={() => setShowKeypad(false)}>
              <FaTimes />
              <span>Cancel</span>
            </button>
          </div>
        </div>
      )}

      <LootCapsule
        key={isBatchConfirmed ? `batch-${capsuleKey}` : 'single'}
        reward={isBatchConfirmed ? currentBatchReward : lastReward}
        isVisible={showCapsule}
        onClose={handleCapsuleClose}
      />

      {showInsufficientTokensPopup && (
        <div className="insufficient-tokens-modal">
          <div
            className="insufficient-tokens-backdrop"
            onClick={handleCloseInsufficientPopup}
          />
          <div className="insufficient-tokens-content">
            <button
              className="insufficient-tokens-close"
              onClick={handleCloseInsufficientPopup}
            >
              <FaTimes />
            </button>
            <div className="insufficient-tokens-icon">
              <FaExclamationTriangle />
            </div>
            <h3 className="insufficient-tokens-title">Insufficient Tokens</h3>
            <p className="insufficient-tokens-message">{getInsufficientMessage()}</p>
            <div className="insufficient-tokens-actions">
              <button
                className="insufficient-tokens-btn primary"
                onClick={
                  shouldShowGetTokens
                    ? handleGoToSlapGenerator
                    : handleCloseInsufficientPopup
                }
              >
                <span>{shouldShowGetTokens ? "Get DCKSLAP" : "Close"}</span>
                {shouldShowGetTokens && <FaArrowRight />}
              </button>
              {shouldShowGetTokens && (
                <button
                  className="insufficient-tokens-btn secondary"
                  onClick={handleCloseInsufficientPopup}
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CandyMachine;
